/**
 * The Form that accepts the access token to authorize a user sign-up
 */
import { isEmpty, trim } from 'lodash';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  H5,
  FormInputText,
  Button,
  Icon,
  P,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import { LoginLayout } from '../Login/LoginLayout';

interface AccessTokenFormProps {
  token?: string | null;
  onSubmit: (accessCode: string) => void;
  loading: boolean;
  error?: string;
  influencer?: boolean;
}

const getErrorDetails = (
  error: string,
): { title: string; description: string | undefined } => {
  let title: string;
  let description: string | undefined;

  switch (error) {
    case 'Invalid invite token or email provided please try again':
      title = 'Incorrect Activation Code';
      description =
        'Please double check the code we sent to you via email and try again.';
      break;
    case 'Invite token has already expired':
      title = 'Activation Code Expired';
      description =
        "We've resent you a new activation code. Please enter it within the next 10 minutes.";
      break;
    case 'Invite token has already been used':
      title = 'Activation Code has already been used';
      description = undefined;
      break;
    default:
      title = error;
      description = undefined;
      break;
  }

  return { title, description };
};

function AccessTokenForm({
  token = null,
  onSubmit,
  loading,
  error = '',
  influencer = false,
}: AccessTokenFormProps) {
  const [accessCode, setAccessCode] = useState<string>(token || '');
  const [errors, setErrors] = useState<{ accessCode: string }>({
    accessCode: '',
  });

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    onSubmit(trim(accessCode));
  };
  useEffect(() => {
    if (token) {
      handleSubmit();
    }
  }, [token]);

  const handleChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setAccessCode(trim(value));
  };

  const handleBlur = () => {
    if (isEmpty(accessCode)) {
      setErrors({ accessCode: 'Please enter your activation code.' });
    }
  };

  return (
    <LoginLayout
      isInfluencer={influencer}
      errorTitle={getErrorDetails(error).title}
      errorDescription={getErrorDetails(error).description}
    >
      <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
        <Stack direction="col">
          <Stack direction="col" className="w-[376px] h-[46px] pb-1">
            <H5>Enter your activation code</H5>
          </Stack>

          <FormInputText
            name="accessCode"
            label="Activation Code"
            value={accessCode}
            inputId="ac-input"
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.accessCode}
            autoComplete="off"
          />

          <Stack direction="col" gap="md" className="mt-9">
            <Button
              className="w-full"
              type="submit"
              disabled={isEmpty(accessCode)}
              loading={loading}
            >
              Continue
              <Icon.ArrowRight />
            </Button>
            <P size="2xs" color="disabled" className="text-center font-medium">
              {`Already have an account? `}
              <Link
                to={influencer ? '/influencer-login' : '/login'}
                className="text-[#5247df]"
              >
                Sign in
              </Link>
            </P>
          </Stack>
        </Stack>
      </form>
    </LoginLayout>
  );
}

export default AccessTokenForm;
